import { useMemo } from 'react'
import { useAccount, useReadContract, useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import vaultFactoryAbi from '@/lib/abis/VaultFactory.json'
import v3VaultAbi from '@/lib/abis/V3Vault.json'
import v4VaultAbi from '@/lib/abis/V4Vault.json'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { toNumber, POLL_SLOW } from '@/lib/utils'
import { PRICE_PRECISION, CLEARING_TWAP_VALUE_ABI, clearingAddressOrZero, priceVaultPosition } from '@/lib/vaultPricing'
import { tagsForPair } from '@/lib/tokenTags'


export const VAULTS_NOT_DEPLOYED =
  !IS_LIVE || (ADDRESSES.VAULT_FACTORY || '').toLowerCase() === ZERO_ADDR

/// Reads VaultFactory.activeVaults() (real-time, matches Horizon's own live-read convention -
/// not a possibly-stale factory-maintained flag) then, per vault, its venue (isV4Vault),
/// IVaultCore-shared fields (name/token0/token1/getTotalAmounts/rangeCount/pause flags/
/// totalSupply/clearing, plus the caller's own balanceOf when connected), and finally - in
/// parallel - each distinct underlying token's symbol/decimals and each vault's own pool TWAP
/// price. Four dependent read stages - each stays disabled until the previous stage resolves,
/// since later stages need addresses the earlier ones produce.
///
/// Pricing: this app only has two DIRECT USD references (USDx at $1, HORIZON via its own
/// farm-pool oracle) - but a vault's OWN pool already prices its two tokens against each other
/// via a manipulation-resistant TWAP (`clearing.twapValue`, the same call RebalanceGuard's NAV
/// floor uses internally). So as long as EITHER side of a vault is a direct reference asset,
/// the other side's USD price is derived from that TWAP ratio instead of being left at $0 - a
/// WETH/USDx or FRONG/HORIZON vault now prices both legs, not just the recognized one. Only a
/// vault pairing two tokens neither of which is a direct reference (or one whose pool has no
/// TWAP history yet) falls back to a partial/raw-amount display - never a fabricated number.
export function useVaultsList() {
  const { address: user } = useAccount()
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const usdxAddr = (ADDRESSES.USDX || '').toLowerCase()
  const horizonAddr = (ADDRESSES.HORIZON_TOKEN || '').toLowerCase()

  // Paged rather than `activeVaults()`, which walks the whole registry twice making an external
  // `depositsPaused()` call per vault on each pass. That is affordable while the set is
  // genesis-curated and single digits, and stops being affordable the moment vault creation opens
  // up: `allVaults` only ever grows, so anyone could push it past a node's `eth_call` gas cap and
  // break this list permanently for everyone. Switching the read BEFORE that change means opening
  // creation is a modifier flip rather than an emergency migration.
  //
  // PAGE_SIZE is deliberately larger than the current set: one request today, and a second only
  // once the registry actually grows past it.
  const PAGE_SIZE = 200n
  const { data: pageRaw, isLoading: loadingVaults } = useReadContract({
    address: ADDRESSES.VAULT_FACTORY, abi: vaultFactoryAbi, functionName: 'vaultsPaged',
    args: [0n, PAGE_SIZE],
    query: { enabled: !VAULTS_NOT_DEPLOYED, refetchInterval: POLL_SLOW },
  })
  const pageAddrs = pageRaw?.[0] ?? []
  const totalVaults = pageRaw?.[1] ?? 0n

  // Second page only when one exists. Kept to two reads rather than a generic loop: a loop needs
  // dynamic hook counts, and 400 vaults is far past the point where this list wants an indexer
  // rather than more pages.
  const { data: page2Raw } = useReadContract({
    address: ADDRESSES.VAULT_FACTORY, abi: vaultFactoryAbi, functionName: 'vaultsPaged',
    args: [PAGE_SIZE, PAGE_SIZE],
    query: { enabled: !VAULTS_NOT_DEPLOYED && totalVaults > PAGE_SIZE, refetchInterval: POLL_SLOW },
  })

  const allAddrs = useMemo(
    () => [...pageAddrs, ...(page2Raw?.[0] ?? [])],
    [pageAddrs, page2Raw],
  )
  const vaults = allAddrs

  const { data: venueReads } = useReadContracts({
    query: { enabled: !VAULTS_NOT_DEPLOYED && vaults.length > 0, refetchInterval: POLL_SLOW },
    contracts: vaults.map((v) => ({
      address: ADDRESSES.VAULT_FACTORY, abi: vaultFactoryAbi, functionName: 'isV4Vault', args: [v],
    })),
  })
  const venues = vaults.map((_, i) => (venueReads?.[i]?.result ? 'v4' : 'v3'))
  const venuesKnown = !!venueReads && venueReads.length === vaults.length

  const { data: coreReads } = useReadContracts({
    query: { enabled: venuesKnown && vaults.length > 0, refetchInterval: POLL_SLOW },
    contracts: vaults.flatMap((v, i) => {
      const abi = venues[i] === 'v4' ? v4VaultAbi : v3VaultAbi
      return [
        { address: v, abi, functionName: 'name' },
        { address: v, abi, functionName: 'symbol' },
        { address: v, abi, functionName: 'token0' },
        { address: v, abi, functionName: 'token1' },
        { address: v, abi, functionName: 'getTotalAmounts' },
        { address: v, abi, functionName: 'rangeCount' },
        { address: v, abi, functionName: 'totalSupply' },
        { address: v, abi, functionName: 'depositsPaused' },
        { address: v, abi, functionName: 'globalPause' },
        { address: v, abi, functionName: 'clearing' },
        ...(user ? [{ address: v, abi, functionName: 'balanceOf', args: [user] }] : []),
      ]
    }),
  })

  const perVaultReadCount = user ? 11 : 10
  const cores = vaults.map((v, i) => {
    const base = i * perVaultReadCount
    if (!coreReads) return null
    return {
      name: coreReads[base + 0]?.result,
      symbol: coreReads[base + 1]?.result,
      token0: coreReads[base + 2]?.result,
      token1: coreReads[base + 3]?.result,
      totalAmounts: coreReads[base + 4]?.result,
      rangeCount: coreReads[base + 5]?.result,
      totalSupply: coreReads[base + 6]?.result,
      depositsPaused: coreReads[base + 7]?.result,
      globalPause: coreReads[base + 8]?.result,
      clearing: coreReads[base + 9]?.result,
      userShares: user ? coreReads[base + 10]?.result : undefined,
    }
  })
  const coresKnown = !!coreReads

  const tokenAddrs = [...new Set(
    cores.flatMap((c) => c ? [c.token0?.toLowerCase(), c.token1?.toLowerCase()] : []).filter(Boolean)
  )]

  const { data: tokenReads } = useReadContracts({
    query: { enabled: coresKnown && tokenAddrs.length > 0, refetchInterval: POLL_SLOW },
    contracts: tokenAddrs.flatMap((addr) => [
      { address: addr, abi: ERC20_ABI, functionName: 'symbol' },
      { address: addr, abi: ERC20_ABI, functionName: 'decimals' },
    ]),
  })
  const tokenMeta = {}
  tokenAddrs.forEach((addr, i) => {
    tokenMeta[addr] = {
      symbol: tokenReads?.[i * 2]?.result ?? (addr.slice(0, 6) + '…' + addr.slice(-4)),
      decimals: tokenReads?.[i * 2 + 1]?.result ?? 18,
    }
  })

  // `twapValue(vault, 1e36, 0)` = mulDiv(1e36, price, 1e36) = price, where price is
  // PriceLib's own PRECISION-scaled raw (wei1-per-wei0) TWAP ratio - a clean way to pull the
  // pool's own price through the existing view function without a venue-specific pool read.
  const { data: priceReads } = useReadContracts({
    query: { enabled: coresKnown && vaults.length > 0, refetchInterval: POLL_SLOW },
    contracts: vaults.map((v, i) => ({
      address: clearingAddressOrZero(cores[i]?.clearing), abi: CLEARING_TWAP_VALUE_ABI, functionName: 'twapValue',
      args: [v, PRICE_PRECISION, 0n],
    })),
  })

  const list = vaults.map((address, i) => {
    const c = cores[i]
    if (!c) return { address, venue: venues[i], loading: true }

    const t0 = c.token0?.toLowerCase()
    const t1 = c.token1?.toLowerCase()
    const meta0 = tokenMeta[t0] ?? { symbol: '…', decimals: 18 }
    const meta1 = tokenMeta[t1] ?? { symbol: '…', decimals: 18 }

    const amt0 = toNumber(c.totalAmounts?.[0] ?? 0n, meta0.decimals)
    const amt1 = toNumber(c.totalAmounts?.[1] ?? 0n, meta1.decimals)

    const { priced0, priced1, tvlUsd, fullyPriced } = priceVaultPosition({
      t0, t1, decimals0: meta0.decimals, decimals1: meta1.decimals, amt0, amt1,
      usdxAddr, horizonAddr, hasHorizonPrice, horizonUsd, unitPriceRaw: priceReads?.[i]?.result,
    })

    const totalSupply = c.totalSupply ?? 0n
    const userShares = c.userShares ?? 0n
    const userSharePct = totalSupply > 0n ? toNumber(userShares) / toNumber(totalSupply) : 0
    const userTvlUsd = tvlUsd * userSharePct

    return {
      address,
      venue: venues[i],
      loading: false,
      name: c.name || `${meta0.symbol}/${meta1.symbol}`,
      symbol: c.symbol,
      token0: { address: c.token0, ...meta0, amount: amt0, priced: priced0 },
      token1: { address: c.token1, ...meta1, amount: amt1, priced: priced1 },
      tvlUsd,
      fullyPriced,
      rangeCount: Number(c.rangeCount ?? 0),
      tags: tagsForPair(t0, t1),
      paused: !!c.depositsPaused || !!c.globalPause,
      hasPosition: !!user && userShares > 0n,
      userShares,
      userTvlUsd,
      userSharePct,
    }
  })

  // `vaultsPaged` returns EVERY vault, where the `activeVaults()` this replaced filtered
  // deposit-paused ones out on chain. Reproducing that filter here keeps the switch to paged reads
  // a pure plumbing change with no visible difference.
  //
  // **One exception, and it is a fix rather than a liberty.** A vault the connected user still has
  // shares in stays listed even while paused. The old behaviour dropped it entirely, which is how a
  // position becomes unreachable from the app - exactly the failure `StakingVaultsPanel` already
  // guards against with "a retired vault stays withdrawable, so it stays visible while anyone is
  // still in it". Withdrawal is never gated by `depositsPaused`, so the row is actionable.
  const visible = list.filter((v) => !v.paused || v.hasPosition)

  return {
    vaults: visible,
    isLoading: loadingVaults || (vaults.length > 0 && !coresKnown),
    notDeployed: VAULTS_NOT_DEPLOYED,
  }
}
